import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface GoogleJwtPayload {
  email: string;
  name: string;
  picture?: string;
  hd?: string;
  exp: number;
}

export interface User {
  email: string;
  name: string;
  picture?: string;
  hd?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'qdf_portal_auth_token';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    if (savedToken) {
      try {
        const decoded = jwtDecode<GoogleJwtPayload>(savedToken);
        if (decoded.exp * 1000 < Date.now()) {
          // Token expired — clear it
          localStorage.removeItem(TOKEN_KEY);
        } else {
          setUser({
            email: decoded.email,
            name: decoded.name,
            picture: decoded.picture,
            hd: decoded.hd,
          });
          setToken(savedToken);
          setIsAuthenticated(true);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = (newToken: string) => {
    try {
      const decoded = jwtDecode<GoogleJwtPayload>(newToken);
      localStorage.setItem(TOKEN_KEY, newToken);
      setUser({
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        hd: decoded.hd,
      });
      setToken(newToken);
      setIsAuthenticated(true);
    } catch {
      // Invalid token — ignore
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
